"use client";
import React, { useState, useEffect } from "react";
import { useMutation, useQueryClient } from 'react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Divider,
  Paper,
  Avatar,
  alpha,
  useTheme,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BadgeIcon from '@mui/icons-material/Badge';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import ToggleSwitchWithLabel from "./ToggleSwitchWithLabel";
import CustomInput from "../../CustomForm/CustomInput";
import FileUpload from "../../CustomForm/FileUpload";
import CustomTextArea from "../../CustomForm/CustomTextArea";

import { createPersonalContact, updatePersonalContact } from "../../../api/services/Client";
import { QK } from "../../../base/qk";

const ContactModal = ({ show, onHide, data = {} }) => {
  const theme = useTheme();
  const [userInput, setUserInput] = useState({});
  const { enqueueSnackbar } = useSnackbar();
  const { id: clientId } = useParams();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (show) {
      if (data?.id) {
        setUserInput({ ...data });
      } else {
        setUserInput({});
      }
    }
  }, [show, data?.id]);

  const { mutate: createContact, isLoading: isCreating } = useMutation(createPersonalContact, {
    onSuccess: () => {
      enqueueSnackbar('Contact created successfully', { variant: 'success' });
      queryClient.invalidateQueries(QK.personalContacts);
      onHide();
    },
  });

  const mutation = useMutation(({ ContactId, UpdateFormData }) => updatePersonalContact(ContactId, UpdateFormData), {
    onSuccess: () => {
      enqueueSnackbar('Personal Contact updated!', { variant: 'success' });
      queryClient.invalidateQueries(QK.personalContacts);
      onHide();
    },
    onError: (error) => enqueueSnackbar(error.message, { variant: 'error' }),
  });

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    const inputValue = type === "file" ? files[0] : (type === "switch" || type === "checkbox") ? checked : value;

    setUserInput(prev => ({
      ...prev,
      [name]: inputValue,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const inputData = new FormData();
    const submittedPayload = { ...userInput, client: clientId };

    Object.keys(submittedPayload).forEach(key => {
      if (submittedPayload[key] !== null && submittedPayload[key] !== undefined) {
        inputData.append(key, submittedPayload[key]);
      }
    });

    if (data?.id) {
      mutation.mutate({ ContactId: data.id, UpdateFormData: inputData });
    } else {
      createContact(inputData);
    }
  };

  const isPending = isCreating || mutation.isLoading;

  return (
    <Dialog
      open={show}
      onClose={onHide}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
              }}
            >
              {data?.id ? <EditNoteIcon sx={{ fontSize: 20 }} /> : <PersonAddAlt1Icon sx={{ fontSize: 20 }} />}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                {data?.id ? "Update Contact" : "Add New Contact"}
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                {data?.id ? `Editing record for ${data.first_name}` : "Enter details for the new personal contact"}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onHide}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, pt: 2 }}>
        <Box component="form" id="contact-form" onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <BadgeIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
              <Typography variant="caption" fontWeight={600} color="#475467" sx={{ textTransform: "uppercase", letterSpacing: "0.3px" }}>
                PERSONAL DETAILS
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ flex: "1 1 calc(50% - 8px)", minWidth: "180px" }}>
                <CustomInput label="First Name *" name="first_name" value={userInput?.first_name || ""} onChange={handleChange} />
              </Box>
              <Box sx={{ flex: "1 1 calc(50% - 8px)", minWidth: "180px" }}>
                <CustomInput label="Last Name *" name="last_name" value={userInput?.last_name || ""} onChange={handleChange} />
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ flex: "1 1 calc(50% - 8px)", minWidth: "180px" }}>
                <CustomInput label="Telephone *" name="telephone" value={userInput?.telephone || ""} onChange={handleChange} />
              </Box>
              <Box sx={{ flex: "1 1 calc(50% - 8px)", minWidth: "180px" }}>
                <CustomInput label="Mobile" name="mobile" value={userInput?.mobile || ""} onChange={handleChange} />
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ flex: "1 1 calc(50% - 8px)", minWidth: "180px" }}>
                <CustomInput label="Relationship *" name="relationship" value={userInput?.relationship || ""} onChange={handleChange} />
              </Box>
              <Box sx={{ flex: "1 1 calc(50% - 8px)", minWidth: "180px" }}>
                <CustomInput label="Email" name="email" value={userInput?.email || ""} onChange={handleChange} />
              </Box>
            </Box>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                borderColor: "#E2E8F0",
                bgcolor: "#FAFBFC",
              }}
            >
              <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                <ContactPageIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                <Typography variant="caption" fontWeight={600} color="#475467" sx={{ textTransform: "uppercase", letterSpacing: "0.3px" }}>
                  LEGAL STATUS & ACCESS
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {[
                  { label: "LPA Health", name: "lpa_health" },
                  { label: "LPA Finance", name: "lpa_finance" },
                  { label: "Deputy Health", name: "deputy_health" },
                  { label: "Deputy Finance", name: "deputy_finance" },
                  { label: "Responsible for Fees", name: "responsible_for_fees" },
                  { label: "Next of Kin?", name: "next_of_kin" },
                  { label: "Emergency Contact?", name: "contact_in_emergency" },
                  { label: "Portal Access?", name: "authorized_portal_user" },
                ].map((sw) => (
                  <Box key={sw.name} sx={{ flex: "1 1 calc(25% - 6px)", minWidth: "140px" }}>
                    <ToggleSwitchWithLabel
                      label={sw.label}
                      name={sw.name}
                      checked={userInput[sw.name] || false}
                      onChange={handleChange}
                    />
                  </Box>
                ))}
              </Box>
            </Paper>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LocationOnIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
              <Typography variant="caption" fontWeight={600} color="#475467" sx={{ textTransform: "uppercase", letterSpacing: "0.3px" }}>
                ADDRESS INFORMATION
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ flex: "1 1 calc(50% - 8px)", minWidth: "180px" }}>
                <CustomInput label="Address 1" name="address_one" value={userInput?.address_one || ""} onChange={handleChange} />
              </Box>
              <Box sx={{ flex: "1 1 calc(50% - 8px)", minWidth: "180px" }}>
                <CustomInput label="Address 2" name="address_two" value={userInput?.address_two || ""} onChange={handleChange} />
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ flex: "1 1 calc(33.33% - 12px)", minWidth: "140px" }}>
                <CustomInput label="Town" name="town" value={userInput?.town || ""} onChange={handleChange} />
              </Box>
              <Box sx={{ flex: "1 1 calc(33.33% - 12px)", minWidth: "140px" }}>
                <CustomInput label="Postcode" name="post_code" value={userInput?.post_code || ""} onChange={handleChange} />
              </Box>
              <Box sx={{ flex: "1 1 calc(33.33% - 12px)", minWidth: "140px" }}>
                <CustomInput label="Country" name="country" value={userInput?.country || ""} onChange={handleChange} />
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ flex: "1 1 calc(50% - 8px)", minWidth: "180px" }}>
                <FileUpload label="Profile Photo" name="profile_photo" value={userInput?.profile_photo || ""} onChange={handleChange} />
              </Box>
              <Box sx={{ flex: "1 1 calc(50% - 8px)", minWidth: "180px" }}>
                <CustomInput label="Caption" name="caption" value={userInput?.caption || ""} onChange={handleChange} />
              </Box>
            </Box>

            <Box>
              <CustomTextArea label="Internal Notes" name="notes" value={userInput?.notes || ""} onChange={handleChange} />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5 }}>
        <Button
          onClick={onHide}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderRadius: "10px",
            borderColor: "#D0D5DD",
            color: "#344054",
            "&:hover": {
              bgcolor: "#F9FAFB",
              borderColor: "#98A2B3",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="contact-form"
          variant="contained"
          disabled={isPending}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            bgcolor: "#2469F0",
            px: 4,
            "&:hover": {
              bgcolor: "#1A5BD4",
              boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
            },
          }}
        >
          {isPending ? <CircularProgress size={20} color="inherit" /> : (data?.id ? "Update Contact" : "Create Contact")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactModal;